// Ops console data layer — staff-only, wired to the cuedesk backend (DIY-JWT).
//
// Auth flow mirrors the customer dashboard's Google sign-in, but the token is
// stored under a distinct key and the app gates on the `platform_role` claim
// (spec 276). The gate is a UI hint only; the backend re-derives it from the DB.

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8008";
const TOKEN_KEY = "cd_ops_jwt"; // distinct from the dashboard's cd_admin_jwt
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function signOut(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** Decode the platform_role claim from the stored JWT (UI gating only). */
export function getPlatformRole(): string | null {
  const tok = getToken();
  if (!tok) return null;
  try {
    const payload = JSON.parse(atob(tok.split(".")[1]));
    return payload.platform_role || null;
  } catch {
    return null;
  }
}

export function isStaff(): boolean {
  const r = getPlatformRole();
  return r === "STAFF" || r === "SUPERADMIN";
}

export function isSuperadmin(): boolean {
  return getPlatformRole() === "SUPERADMIN";
}

/** Operator identity for the sidebar footer, decoded from the stored JWT. */
export function getOperator(): { name: string; email: string | null; initials: string } {
  const tok = getToken();
  let email: string | null = null;
  let name = "";
  if (tok) {
    try {
      const p = JSON.parse(atob(tok.split(".")[1]));
      email = p.email || null;
      name = p.name || "";
    } catch {
      /* ignore */
    }
  }
  const display = name || (email ? email.split("@")[0] : "Operator");
  const initials =
    display
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w: string) => w[0]?.toUpperCase() || "")
      .join("") || "OP";
  return { name: display, email, initials };
}

/** Human label for the environment pill, inferred from the API base host. */
export function getEnvLabel(): string {
  const b = API_BASE.toLowerCase();
  if (b.includes("localhost") || b.includes("127.0.0.1") || b.includes("8008")) return "local";
  if (b.includes("staging")) return "staging";
  return "production";
}

function loadGis(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).google?.accounts?.oauth2) return resolve();
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Google sign-in"));
    document.head.appendChild(s);
  });
}

/** Google Identity Services → exchange for a cuedesk DIY JWT via the same
 * /auth/google/token endpoint the extension and dashboard use. The backend
 * bootstraps/blocks the account and stamps platform_role into the token. */
export async function signInWithGoogle(): Promise<void> {
  if (!GOOGLE_CLIENT_ID) throw new Error("REACT_APP_GOOGLE_CLIENT_ID is not set");
  await loadGis();
  const accessToken = await new Promise<string>((resolve, reject) => {
    const client = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: "openid email profile",
      // UX hint only — the backend enforces OPS_ALLOWED_EMAIL_DOMAINS.
      ...(process.env.REACT_APP_GOOGLE_HD ? { hd: process.env.REACT_APP_GOOGLE_HD } : {}),
      callback: (resp: any) =>
        resp.error ? reject(new Error(resp.error)) : resolve(resp.access_token),
    });
    client.requestAccessToken();
  });
  const resp = await fetch(`${API_BASE}/auth/google/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ access_token: accessToken }),
  });
  if (resp.status === 403) throw new Error("This account is not authorized for ops access.");
  if (!resp.ok) throw new Error(`Auth exchange failed (${resp.status})`);
  const data = await resp.json();
  try {
    localStorage.setItem(TOKEN_KEY, data.token);
  } catch {
    /* ignore */
  }
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function fetchWithAuth(path: string, init: RequestInit = {}): Promise<any> {
  const tok = getToken();
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  if (tok) headers["Authorization"] = `Bearer ${tok}`;
  if (init.body) headers["Content-Type"] = "application/json";
  const resp = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (resp.status === 401) {
    // Token revoked (e.g. our own staff access was pulled) — force re-auth.
    signOut();
    throw new ApiError(401, "Session expired — please sign in again.");
  }
  if (!resp.ok) {
    let detail = `Request failed (${resp.status})`;
    try {
      const body = await resp.json();
      if (body?.detail) detail = typeof body.detail === "string" ? body.detail : detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(resp.status, detail);
  }
  if (resp.status === 204) return null;
  return resp.json();
}

// --- types -----------------------------------------------------------------

export type AccountStatus = "active" | "suspended" | "disabled";
export type SystemRole = "owner" | "admin" | "manager" | "member";
export type PlatformRole = "NONE" | "STAFF" | "SUPERADMIN";

export interface UserRow {
  id: string;
  name: string | null;
  email: string | null;
  status: AccountStatus;
  platform_role: PlatformRole;
  org_count: number;
}

export interface MembershipView {
  org_id: string;
  org_name: string | null;
  system_role: SystemRole;
  is_personal?: boolean;
  // spec 688 (B2): does the user have a verified caller ID in this org?
  has_verified_caller_id?: boolean;
}

export interface UserDetail {
  id: string;
  name: string | null;
  email: string | null;
  status: AccountStatus;
  platform_role: PlatformRole;
  created_at: string | null;
  recent_call_count: number;
  memberships: MembershipView[];
}

export interface OrgRow {
  id: string;
  name: string | null;
  is_personal: boolean;
  plan_tier: string | null;
  seat_limit: number | null;
  // spec 882: per-class seat pools (manager $39 / member $19); null = no class split.
  manager_seat_limit: number | null;
  member_seat_limit: number | null;
  member_count: number;
  trial_status: "none" | "active" | "lapsed";
  trial_ends_at: string | null;
  // spec 688 (B1): dialing state on the list row for the Orgs "Dialing" column.
  dialing_disabled_at?: string | null;
  dialing_enabled?: boolean;
}

export interface OrgOwner {
  user_id: string;
  name: string | null;
  email: string | null;
}

export interface OrgDetail extends OrgRow {
  created_at: string | null;
  trial_reconciled_at: string | null;
  owners: OrgOwner[];
}

/** Balance in seconds, resolved from getOrgCreditBalance — carried on OrgDetail
 * views client-side (not part of the org read). */
export interface OrgWithBalance extends OrgDetail {
  balance_seconds?: number | null;
}

export interface CreateOrgBody {
  name: string;
  plan_tier: string;
  seat_limit: number;
  trial_days: number;
  owner_user_id: string;
}

export interface UpdateOrgBody {
  /** Spec 753: rename (superadmin, non-personal orgs; absent = untouched). */
  name?: string;
  plan_tier?: string;
  seat_limit?: number;
  /** Spec 882: per-class seat pools. Explicit null clears the split (absent = untouched). */
  manager_seat_limit?: number | null;
  member_seat_limit?: number | null;
  trial_ends_at?: string | null;
}

export interface AuditEntry {
  id: string;
  actor_user_id: string | null;
  action: string;
  target_user_id: string | null;
  target_org_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  created_at: string | null;
  // spec 688 (B3): resolved display names for the Audit page.
  actor_name?: string | null;
  target_label?: string | null;
}

// --- spec 688: readiness / summary / conversations / reconciliation ---------

export type GateState = "pass" | "fail" | "na";

export interface ReadinessGate {
  key: string;
  label: string;
  state: GateState;
  detail: string;
  reason: string;
}

export interface ReadinessResult {
  org_id: string;
  org_name: string | null;
  user_id: string | null;
  user_name: string | null;
  gates: ReadinessGate[];
  can_dial: boolean;
  reasons: string[];
}

export interface Summary {
  organizations_total: number;
  users_total: number;
  dialing_disabled_count: number;
  low_balance_count: number;
}

/** The ONLY conversation fields ops may see — metadata, never content. */
export interface ConversationMetadata {
  id: string;
  org_id: string;
  org_name: string | null;
  owner_id: string | null;
  owner_name: string | null;
  owner_email: string | null;
  source: string | null;
  dialed_number: string | null;
  status: string | null;
  created_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  call_sid: string | null;
}

export interface ReconciliationRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  window_start: string;
  window_end: string;
  calls_checked: number;
  corrections: number;
  discrepancies: number;
  unattributable: number;
  status: string;
  detail: string | null;
}

export interface AnalyticsHealth {
  environment: string;
  forwarding_configured: boolean;
  profiles_enabled: boolean;
  events_recorded_last_hour: number;
  last_forward_success: string | null;
  last_forward_failure: string | null;
  consecutive_failures: number;
  last_error: string | null;
  stale: boolean;
}

// --- ops calls -------------------------------------------------------------

/** Analytics forwarding health (spec 491 US4). `stale` == recording-not-forwarding. */
export function getAnalyticsHealth(): Promise<AnalyticsHealth> {
  return fetchWithAuth(`/api/ops/analytics-health`);
}

export function searchUsers(
  q: string,
  includeDisabled = false,
): Promise<{ users: UserRow[]; total: number }> {
  const params = new URLSearchParams({ q, include_disabled: String(includeDisabled) });
  return fetchWithAuth(`/api/ops/users?${params}`);
}

export function getUser(userId: string): Promise<UserDetail> {
  return fetchWithAuth(`/api/ops/users/${encodeURIComponent(userId)}`);
}

export function searchOrgs(q: string): Promise<{ orgs: OrgRow[]; total: number }> {
  return fetchWithAuth(`/api/ops/orgs?${new URLSearchParams({ q })}`);
}

export function getOrg(orgId: string): Promise<OrgDetail> {
  return fetchWithAuth(`/api/ops/orgs/${encodeURIComponent(orgId)}`);
}

export function createOrg(body: CreateOrgBody): Promise<OrgDetail> {
  return fetchWithAuth(`/api/ops/orgs`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateOrg(orgId: string, body: UpdateOrgBody): Promise<OrgDetail> {
  return fetchWithAuth(`/api/ops/orgs/${encodeURIComponent(orgId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// --- telephony credits (spec 683) ------------------------------------------

export interface OrgCreditBalance {
  org_id: string;
  balance_seconds: number;
}

export type CreditDirection = "add" | "subtract";

export interface CreditAdjustmentResult {
  ledger_id: string;
  balance_seconds: number;
  entry_type: string;
}

/** Org prepaid call-credit balance (staff read). Rejects with ApiError 404 when
 * telephony is disabled for the environment. */
export function getOrgCreditBalance(orgId: string): Promise<OrgCreditBalance> {
  return fetchWithAuth(`/api/ops/telephony/orgs/${encodeURIComponent(orgId)}/balance`);
}

/** Add or subtract org call credits (superadmin). `seconds` must be > 0; the
 * caller converts the operator's minutes to seconds (minutes × 60). */
export function adjustOrgCredits(
  orgId: string,
  body: { direction: CreditDirection; seconds: number; reason: string },
): Promise<CreditAdjustmentResult> {
  return fetchWithAuth(`/api/ops/telephony/adjustments`, {
    method: "POST",
    body: JSON.stringify({ org_id: orgId, ...body }),
  });
}

export function listAudit(
  filter: { targetUserId?: string; targetOrgId?: string } = {},
): Promise<{ entries: AuditEntry[]; total: number }> {
  const params = new URLSearchParams();
  if (filter.targetUserId) params.set("target_user_id", filter.targetUserId);
  if (filter.targetOrgId) params.set("target_org_id", filter.targetOrgId);
  return fetchWithAuth(`/api/ops/audit?${params}`);
}

// --- spec 688: readiness / summary / dialing / conversations / reconciliation

/** Platform summary counts for the Support home (staff read). */
export function getSummary(): Promise<Summary> {
  return fetchWithAuth(`/api/ops/summary`);
}

export interface OrgMember {
  id: string;
  name: string | null;
  email: string | null;
  system_role: SystemRole | null;
}

/** Members of an org (staff read) — scopes the readiness user selector. */
export function listOrgMembers(orgId: string): Promise<{ members: OrgMember[] }> {
  return fetchWithAuth(`/api/ops/orgs/${encodeURIComponent(orgId)}/members`);
}

/** Dialing-readiness diagnostic (staff read, side-effect-free). */
export function getReadiness(orgId: string, userId?: string): Promise<ReadinessResult> {
  const params = new URLSearchParams({ org_id: orgId });
  if (userId) params.set("user_id", userId);
  return fetchWithAuth(`/api/ops/telephony/readiness?${params}`);
}

/** Kill-switch: enable/disable dialing for an org with a required, audited reason
 * (superadmin). Returns the updated org detail. */
export function setOrgDialing(
  orgId: string,
  body: { disabled: boolean; reason: string },
): Promise<OrgDetail> {
  return fetchWithAuth(`/api/ops/orgs/${encodeURIComponent(orgId)}/dialing`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Cross-org conversation metadata by id (superadmin, audited, METADATA ONLY). */
export function getConversation(conversationId: string): Promise<ConversationMetadata> {
  return fetchWithAuth(`/api/ops/conversations/${encodeURIComponent(conversationId)}`);
}

/** An org's recent conversations, metadata only (superadmin, audited). */
export function listOrgConversations(
  orgId: string,
  limit = 20,
): Promise<{ conversations: ConversationMetadata[] }> {
  const params = new URLSearchParams({ org_id: orgId, limit: String(limit) });
  return fetchWithAuth(`/api/ops/conversations?${params}`);
}

/** Telephony ledger reconciliation runs (staff read, spec 485/688 US5). */
export function listReconciliationRuns(limit = 30): Promise<{ runs: ReconciliationRun[] }> {
  return fetchWithAuth(`/api/ops/telephony/reconciliation-runs?limit=${limit}`);
}

export function changeRole(userId: string, orgId: string, role: SystemRole) {
  return fetchWithAuth(
    `/api/ops/users/${encodeURIComponent(userId)}/memberships/${encodeURIComponent(orgId)}`,
    { method: "PATCH", body: JSON.stringify({ system_role: role }) },
  );
}

export function addMembership(userId: string, orgId: string, role: SystemRole) {
  return fetchWithAuth(`/api/ops/users/${encodeURIComponent(userId)}/memberships`, {
    method: "POST",
    body: JSON.stringify({ org_id: orgId, system_role: role }),
  });
}

export function removeMembership(userId: string, orgId: string) {
  return fetchWithAuth(
    `/api/ops/users/${encodeURIComponent(userId)}/memberships/${encodeURIComponent(orgId)}`,
    { method: "DELETE" },
  );
}

export function suspendUser(userId: string, reason: string) {
  return fetchWithAuth(`/api/ops/users/${encodeURIComponent(userId)}/suspend`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function unsuspendUser(userId: string) {
  return fetchWithAuth(`/api/ops/users/${encodeURIComponent(userId)}/unsuspend`, { method: "POST" });
}

export function disableUser(userId: string, reason: string) {
  return fetchWithAuth(`/api/ops/users/${encodeURIComponent(userId)}/disable`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function restoreUser(userId: string) {
  return fetchWithAuth(`/api/ops/users/${encodeURIComponent(userId)}/restore`, { method: "POST" });
}

export function setPlatformRole(userId: string, role: PlatformRole) {
  return fetchWithAuth(`/api/ops/users/${encodeURIComponent(userId)}/platform-role`, {
    method: "POST",
    body: JSON.stringify({ platform_role: role }),
  });
}

// --- spec 690: cost & unit economics (backend spec 689 / #722) --------------

/** Usage the backend could not price (no matching rate) — surfaced, never $0. */
export interface Unpriced {
  category: string;
  model_key: string;
  tokens?: number;
  minutes?: number;
}

/** Company-wide modeled cost + unit economics for a period (`/summary`). */
export interface CostSummary {
  period_start: string;
  period_end: string;
  llm_usd: number;
  telephony_usd: number;
  stt_usd: number;
  total_variable_usd: number;
  // usd is prorated to the period; monthly_usd is the maintained company figure.
  fixed_infra: { status: string; usd: number | null; monthly_usd?: number | null };
  calls: number;
  active_orgs: number;
  cost_per_call: number | null;
  cost_per_minute: number | null; // per TELEPHONY minute
  cost_per_active_org: number | null;
  unpriced: Unpriced[];
  source: string; // "modeled"
  generated_at: string;
}

/** One org's modeled cost + revenue + margin for the period. */
export interface OrgCostRow {
  org_id: string;
  org_name: string | null;
  plan_tier: string | null;
  llm_usd: number;
  telephony_usd: number;
  stt_usd: number;
  total_variable_usd: number;
  calls: number;
  telephony_minutes: number;
  stt_minutes: number;
  tokens_prompt: number;
  tokens_completion: number;
  tokens_cached: number;
  revenue_usd: number | null;
  margin_usd: number | null;
  /** Authoritative "revenue unknown" signal — render n/a off THIS, not a null check. */
  revenue_known: boolean;
}

export interface CostByOrg {
  period_start: string;
  period_end: string;
  orgs: OrgCostRow[];
  total_variable_usd: number;
  unpriced: Unpriced[];
  source: string;
}

/** One editable unit rate (unit_cost_usd is a decimal string, not a float). */
export interface RateRow {
  id: string;
  category: string;
  model_key: string;
  unit: string;
  unit_cost_usd: string;
  note: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface RateCard {
  rates: RateRow[];
}

/** Company cost + unit economics (staff read). `params` from cost.periodParams(). */
export function getCostSummary(params: Record<string, string>): Promise<CostSummary> {
  return fetchWithAuth(`/api/ops/cost/summary?${new URLSearchParams(params)}`);
}

/** Per-org cost + revenue + margin, highest cost first (staff read). */
export function getCostByOrg(params: Record<string, string>): Promise<CostByOrg> {
  return fetchWithAuth(`/api/ops/cost/by-org?${new URLSearchParams(params)}`);
}

/** The maintained rate card (staff read). */
export function getRateCard(): Promise<RateCard> {
  return fetchWithAuth(`/api/ops/cost/rate-card`);
}

/** Edit a unit rate (superadmin, audited). Returns only the updated row —
 * the caller re-fetches summary + by-org to recompute displayed costs. */
export function updateRate(
  rateId: string,
  body: { unit_cost_usd: string; note?: string },
): Promise<RateRow> {
  return fetchWithAuth(`/api/ops/cost/rate-card/${encodeURIComponent(rateId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** Add a rate row for a newly-deployed model (superadmin, audited). The unit is
 * derived server-side from the category; llm_* require a model_key. */
export function createRate(body: {
  category: string;
  model_key?: string;
  unit_cost_usd: string;
  note?: string;
}): Promise<RateRow> {
  return fetchWithAuth(`/api/ops/cost/rate-card`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Delete a rate row (superadmin, audited). Its usage falls back to unpriced. */
export function deleteRate(rateId: string): Promise<{ deleted: boolean; id: string }> {
  return fetchWithAuth(`/api/ops/cost/rate-card/${encodeURIComponent(rateId)}`, {
    method: "DELETE",
  });
}
